import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import {Box, Button, Checkbox, Chip, FormControl, IconButton, InputAdornment, InputLabel, ListItemText, MenuItem, Select, TextField, Tooltip, Typography} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from '@mui/icons-material/Download';
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import InboxIcon from "@mui/icons-material/Inbox";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import DensityLargeIcon from "@mui/icons-material/DensityLarge";
import {useEffect, useState} from "react";
import {ClearIcon} from "@mui/x-date-pickers";
import EmptyState from "../../../components/state/EmptyState";

const DENSITIES = ['compact', 'standard', 'comfortable'];
const DENSITY_ICON = {compact: DensitySmallIcon, standard: DensityMediumIcon, comfortable: DensityLargeIcon};

const DEFAULT_PAGE_SIZE = 25;

export default function SoldBoxesByCustomerAndArticleTable({
    rows, columns, navigateToRowObjectScreen=undefined, columnsRenderer={}, emptyState=undefined,
    quickFilters=[], queryFilters=[], selectFilters=[], onVisibleRowsChange=undefined, pageSize=DEFAULT_PAGE_SIZE, pageSizeOptions=[DEFAULT_PAGE_SIZE],
    emphasizedRowPredicate=undefined,
}) {
    const [filters, setFilters] = useState({});
    const [globalQuery, setGlobalQuery] = useState("");
    const [density, setDensity] = useState('standard');
    const [activeQuickFilters, setActiveQuickFilters] = useState([]);

    const cellText = (row, field) => {
        const cellValue = row[field];
        return typeof cellValue === 'object' && cellValue?.urlName
            ? cellValue.urlName
            : cellValue?.toString?.() || '';
    };

    const cycleDensity = () => {
        setDensity((current) => DENSITIES[(DENSITIES.indexOf(current) + 1) % DENSITIES.length]);
    };

    const DensityIcon = DENSITY_ICON[density];

    function booleanRenderer(boolean) {
        return <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
            {boolean ? <CheckCircleIcon sx={{color: 'success.main'}}/> : <CancelIcon sx={{color: 'error.main'}}/>}
        </div>
    }

    const handleFilterChange = (field, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [field]: value.toLowerCase(),
        }));
    };

    const hasAnyFilterChip = quickFilters.length > 0 || queryFilters.length > 0;

    const optionLabelOf = (selectFilter, value) =>
        selectFilter.options.find((option) => option.value === value).label;

    const selectionSummaryOf = (selectFilter, selectedValues) => {
        const selectedLabels = selectedValues.map((value) => optionLabelOf(selectFilter, value));
        const remainingCount = selectedLabels.length - 1;
        return remainingCount > 0 ? `${selectedLabels[0]} +${remainingCount}` : selectedLabels[0];
    };

    const activeSelectFilterChips = () =>
        selectFilters.flatMap((selectFilter) => selectFilter.selectedValues.map((value) => ({
            key: `${selectFilter.label}:${value}`,
            label: `${selectFilter.label}: ${optionLabelOf(selectFilter, value)}`,
            remove: () => selectFilter.onChange(selectFilter.selectedValues.filter((selectedValue) => selectedValue !== value)),
        })));
    const columnFields = columns.map((column) => column.id);
    const normalizedGlobalQuery = globalQuery.trim().toLowerCase();

    const matchesColumnFilters = (row) =>
        Object.entries(filters).every(([field, value]) => !value || cellText(row, field).toLowerCase().includes(value.toLowerCase()));

    const matchesGlobalQuery = (row) =>
        !normalizedGlobalQuery || columnFields.some((field) => cellText(row, field).toLowerCase().includes(normalizedGlobalQuery));

    const matchesQuickFilters = (row) =>
        activeQuickFilters.every((index) => quickFilters[index].predicate(row));

    const toggleQuickFilter = (index) => {
        setActiveQuickFilters((current) =>
            current.includes(index) ? current.filter((activeIndex) => activeIndex !== index) : [...current, index]);
    };

    const renderFilterChip = ({label, icon, isActive, onToggle}) => (
        <Chip
            key={label}
            label={label}
            icon={icon}
            onClick={onToggle}
            onDelete={isActive ? onToggle : undefined}
            color={isActive ? 'primary' : 'default'}
            variant={isActive ? 'filled' : 'outlined'}
            size="small"
        />
    );

    const filteredRows = rows.filter((row) => matchesColumnFilters(row) && matchesGlobalQuery(row) && matchesQuickFilters(row));

    useEffect(() => {
        if (onVisibleRowsChange) {
            onVisibleRowsChange(filteredRows);
        }
    }, [filteredRows, onVisibleRowsChange]);

    const isDateFormat = (value) => {
        return typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value);
    };

    const isURLName = (value) => {
        return value && typeof value === "object" && typeof value.urlName === "string";
    };

    const isMoneyFormat = (value) => {
        return typeof value === "string" && /^-?\s*\$/.test(value.trim());
    };

    function URLComparator(aColumnContent, anotherColumnContent) {
        const aURLName = anotherColumnContent.urlName;
        const anotherURLName = aColumnContent.urlName;

        const isCodeFormat = /^[A-Za-z0-9]{2}-\d{6}$/.test(anotherURLName) && /^[A-Za-z0-9]{2}-\d{6}$/.test(aURLName);
        if (isCodeFormat) {
            const [prefixA, numA] = anotherURLName.split('-');
            const [prefixB, numB] = aURLName.split('-');
            const prefixCompare = prefixA.localeCompare(prefixB);
            if (prefixCompare !== 0) return prefixCompare;
            return parseInt(numA) - parseInt(numB);
        }
        return anotherURLName.localeCompare(aURLName);
    }

    const dateComparator = (aColumnContent, anotherColumnContent) => {
        const parseDate = (str) => {
            const [day, month, year] = str.split('-').map(Number);
            return new Date(year, month - 1, day);
        };
        const aDate = parseDate(aColumnContent);
        const anotherDate = parseDate(anotherColumnContent);
        return aDate - anotherDate;
    }

    const moneyComparator = (aColumnContent, anotherColumnContent) => {
        const parseMoneyAmount = (formattedMoney) => {
            const digitsSignAndDecimalComma = formattedMoney.replace(/[^\d,-]/g, '');
            return parseFloat(digitsSignAndDecimalComma.replace(',', '.'));
        };
        return parseMoneyAmount(aColumnContent) - parseMoneyAmount(anotherColumnContent);
    }

    function tableColumns() {
        return columns.map((column) => {
            const columnId = column.id;
            const dataGridColumn = {
                field: columnId,
                headerName: column.label,
                flex: 1,
                minWidth: 140,
                align: 'center',
                headerAlign: 'center',
                renderHeader: () => (
                    <Tooltip title={column.label} arrow>
                        <Box>
                            <TextField
                                variant="standard"
                                placeholder={column.label}
                                value={filters[columnId] || ""}
                                onChange={(event) => handleFilterChange(columnId, event.target.value)}
                                sx={{ width: '100%', fontSize: '2rem' }}
                                onClick={(event) => event.stopPropagation()}
                                InputProps={{
                                    endAdornment: filters[columnId] ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => handleFilterChange(columnId, "")}
                                                edge="end"
                                                size="small"
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Box>
                    </Tooltip>

                ),
            };

            if (columnId in columnsRenderer) {
                dataGridColumn.renderCell = columnsRenderer[columnId]
            } else {
                dataGridColumn.renderCell = (params) => {
                    {
                        if (typeof params.value === 'boolean') {
                            return booleanRenderer(params.value)
                        }
                        if (params.value?.hasFile && params.value.fileUrl) {
                            return (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <IconButton
                                        color="primary"
                                        onClick={() => window.open(params.value.fileUrl, '_blank')}
                                        aria-label="Descargar archivo"
                                    >
                                        <DownloadIcon />
                                    </IconButton>
                                </div>
                            );
                        }
                    }
                }
            }

                dataGridColumn.sortComparator = (aColumnContent, anotherColumnContent) => {
                    if (isDateFormat(aColumnContent) && isDateFormat(anotherColumnContent)) {
                        return dateComparator(aColumnContent, anotherColumnContent);
                    }
                    if (isURLName(aColumnContent) && isURLName(anotherColumnContent)) {
                        return URLComparator(aColumnContent, anotherColumnContent);
                    }
                    if (isMoneyFormat(aColumnContent) && isMoneyFormat(anotherColumnContent)) {
                        return moneyComparator(aColumnContent, anotherColumnContent);
                    }
                    if (typeof aColumnContent === "string" && typeof anotherColumnContent === "string") {
                        return aColumnContent.localeCompare(anotherColumnContent);
                    }
                    if (typeof aColumnContent === "number" && typeof anotherColumnContent === "number") {
                        return aColumnContent - anotherColumnContent;
                    }
                    return JSON.stringify(aColumnContent).localeCompare(JSON.stringify(anotherColumnContent));
                };
                return dataGridColumn;
            }
        )
    }

    const hasActiveFilters = Boolean(normalizedGlobalQuery) || Object.values(filters).some((value) => value) ||
        activeQuickFilters.length > 0 || activeSelectFilterChips().length > 0;

    const clearAllFilters = () => {
        setFilters({});
        setGlobalQuery("");
        setActiveQuickFilters([]);
        selectFilters.forEach((selectFilter) => selectFilter.onChange([]));
    };

    const renderEmptyState = () => (
        <Box sx={{border: '0.5px solid', borderColor: 'divider', borderRadius: '12px', backgroundColor: 'background.paper'}}>
            {hasActiveFilters ? (
                <EmptyState
                    icon={<SearchOffIcon/>}
                    title="Sin resultados"
                    description="No hay filas que coincidan con los filtros aplicados."
                    action={<Button variant="outlined" onClick={clearAllFilters}>Limpiar filtros</Button>}
                />
            ) : (
                <EmptyState
                    icon={emptyState?.icon || <InboxIcon/>}
                    title={emptyState?.title || "No hay datos para mostrar"}
                    description={emptyState?.description}
                    action={emptyState?.action}
                />
            )}
        </Box>
    );

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
                <TextField
                    size="small"
                    placeholder="Buscar en la tabla…"
                    value={globalQuery}
                    onChange={(event) => setGlobalQuery(event.target.value)}
                    sx={{width: 280}}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                            </InputAdornment>
                        ),
                        endAdornment: globalQuery ? (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setGlobalQuery("")} edge="end" size="small" aria-label="Limpiar búsqueda">
                                    <ClearIcon fontSize="small"/>
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                {selectFilters.map((selectFilter) => (
                    <FormControl key={selectFilter.label} size="small" sx={{width: 170}}>
                        <InputLabel id={`table-filter-${selectFilter.label}`}>{selectFilter.label}</InputLabel>
                        <Select
                            multiple
                            labelId={`table-filter-${selectFilter.label}`}
                            label={selectFilter.label}
                            value={selectFilter.selectedValues}
                            onChange={(event) => selectFilter.onChange(event.target.value)}
                            renderValue={(selectedValues) => selectionSummaryOf(selectFilter, selectedValues)}
                        >
                            {selectFilter.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    <Checkbox size="small" checked={selectFilter.selectedValues.includes(option.value)}/>
                                    <ListItemText primary={option.label}/>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ))}
                {hasAnyFilterChip && (
                    <FilterListIcon fontSize="small" sx={{color: 'text.secondary', ml: 0.5}}/>
                )}
                {quickFilters.map((quickFilter, index) => renderFilterChip({
                    label: quickFilter.label,
                    icon: quickFilter.icon,
                    isActive: activeQuickFilters.includes(index),
                    onToggle: () => toggleQuickFilter(index),
                }))}
                {queryFilters.map((queryFilter) => renderFilterChip(queryFilter))}
                <Box sx={{flexGrow: 1}}/>
                <Typography variant="body2" sx={{color: 'text.secondary'}}>
                    {filteredRows.length} {filteredRows.length === 1 ? 'resultado' : 'resultados'}
                </Typography>
                <Tooltip title={`Densidad: ${density}`}>
                    <IconButton onClick={cycleDensity} size="small" aria-label="Cambiar densidad">
                        <DensityIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5, '&:empty': {display: 'none'}}}>
                {activeSelectFilterChips().map((chip) => (
                    <Chip key={chip.key} label={chip.label} onDelete={chip.remove} size="small"/>
                ))}
            </Box>
            {filteredRows.length === 0 ? renderEmptyState() : (
            <DataGrid
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                rows={filteredRows}
                columns={tableColumns()}
                density={density}
                initialState={{pagination: {paginationModel: {pageSize}}}}
                pageSizeOptions={pageSizeOptions}
                onRowClick={navigateToRowObjectScreen ? navigateToRowObjectScreen : () => {}}
                disableRowSelectionOnClick
                disableColumnFilter
                getRowClassName={(params) => (
                    emphasizedRowPredicate && emphasizedRowPredicate(params.row) ? 'sapore-emphasized-row' : ''
                )}
                sx={{
                    '.MuiDataGrid-row': {
                        cursor: navigateToRowObjectScreen ? 'pointer' : 'auto',
                    },
                    '.MuiDataGrid-cell:focus': {
                        outline: 'none',
                    },
                    '.MuiDataGrid-row:focus-within': {
                        outline: 'none',
                    },
                    '.sapore-emphasized-row': {
                        fontWeight: 'bold',
                    },
                }}
            />
            )}
        </Box>
    );
}
